import React, { useState } from "react";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import AIChat from "../../components/AIChat";
import faqData from "../../data/faqData.json";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface FAQData {
  [key: string]: {
    en: FAQItem[];
    vn: FAQItem[];
  };
}

const FAQ: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("REGISTRATION");
  const [language, setLanguage] = useState<"en" | "vn">("en");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Danh sách nhóm câu hỏi lấy theo thứ tự trong faqData.json
  const categories = Object.keys(faqData);

  const getCurrentFAQs = (): FAQItem[] => {
    const categoryData = faqData[selectedCategory as keyof typeof faqData];
    if (!categoryData) return [];

    const faqs = categoryData[language] || [];

    if (!searchQuery) return faqs;

    return faqs.filter(
      (faq: FAQItem) =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const currentFAQs = getCurrentFAQs();

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-white">
        {/* Search Bar and Controls Row */}
        <div className="mb-8">
          {/* Top Row - Search and Controls */}
          <div className="flex items-center gap-4 mb-4">
            {/* Search Bar */}
            <div className="flex-1 relative max-w-md">
              <input
                type="text"
                placeholder="Which is your issues?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-12 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300 text-gray-700"
              />
              <svg
                className="w-5 h-5 text-gray-500 absolute left-4 top-1/2 transform -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            <div className="flex items-center gap-4 ml-auto">
              {/* Language Toggle */}
              <div className="flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setLanguage("en")}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                    language === "en"
                      ? "bg-black text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage("vn")}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                    language === "vn"
                      ? "bg-black text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  VN
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Category Selector */}
          <div className="lg:hidden mb-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-3 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-300 text-gray-700 text-sm font-medium"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="lg:grid lg:grid-cols-10 lg:gap-8">
          {/* Left Categories Sidebar - 2/10 width - Hidden on mobile */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="space-y-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`w-full text-left px-0 py-3 text-sm transition-colors border-b border-gray-200 ${
                    selectedCategory === category
                      ? "text-black font-semibold border-black border-b-2"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Content - 8/10 width on desktop, full width on mobile */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {currentFAQs.map((faq: FAQItem) => (
                <div key={faq.id} className="bg-white">
                  <button
                    onClick={() => toggleExpanded(faq.id)}
                    className="w-full p-4 text-left flex items-start justify-between hover:bg-gray-50 transition-colors border-b border-gray-100"
                  >
                    <span className="text-sm font-medium text-gray-900 pr-4 leading-relaxed">
                      {faq.question}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 mt-0.5 ${
                        expandedItems.has(faq.id) ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedItems.has(faq.id) && (
                    <div className="px-4 pb-6 border-b border-gray-100 text-left">
                      <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {currentFAQs.length === 0 && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No results found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or browse different categories.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FAQ;
